import "core-js/modules/es.iterator.constructor.js";
import "core-js/modules/es.iterator.map.js";
import { provide, reactive, ref, toRefs, unref } from 'vue';
import { omit } from '@/utils';
import { XButton } from '@xui/components/base';
import XModal from './xModal.vue';
import locale from 'ant-design-vue/es/locale/zh_CN';
import 'dayjs/locale/zh-cn';
import dayjs from 'dayjs';
import theme from '@/antvTheme';
import { ConfigProvider } from 'ant-design-vue';
dayjs.locale('zh-cn');
const componentSize = ref('middle');
const appTheme = ref({
  ...theme
});
export default {
  name: 'XComponentModal',
  components: {
    ConfigProvider,
    XModal,
    XButton
  },
  props: {
    title: {
      type: String,
      default: ''
    },
    width: {
      type: [String, Number],
      default: '50%'
    },
    confirmLoading: {
      type: Boolean,
      default: false
    },
    okText: {
      type: String,
      default: '确定'
    },
    cancelText: {
      type: String,
      default: '取消'
    },
    open: {
      type: Boolean,
      default: false
    },
    cancelClosable: {
      type: Boolean,
      default: true
    },
    okClosable: {
      type: Boolean,
      default: false
    },
    showFooter: {
      type: Boolean,
      default: true
    },
    showMaximize: {
      type: Boolean,
      default: true
    },
    showMinimize: {
      type: Boolean,
      default: false
    },
    showClose: {
      type: Boolean,
      default: true
    },
    destroyOnClose: {
      type: Boolean,
      default: true
    },
    transfer: {
      type: Boolean,
      default: false
    },
    draggable: {
      type: Boolean,
      default: true
    },
    resize: {
      type: Boolean,
      default: false
    },
    keyboard: {
      type: Boolean,
      default: true
    },
    lockView: {
      type: Boolean,
      default: true
    },
    mask: {
      type: Boolean,
      default: true
    },
    maskClosable: {
      type: Boolean,
      default: true
    },
    showOkButton: {
      type: Boolean,
      default: true
    },
    showCancelButton: {
      type: Boolean,
      default: true
    },
    fullscreen: {
      type: Boolean,
      default: false
    },
    params: {
      type: Object,
      default: () => ({})
    },
    beforeHide: {
      type: Function
    }
  },
  emits: ['ok', 'cancel', 'close', 'update:open', 'show', 'hide', 'zoom', 'move', 'resize'],
  setup(props, {
    emit,
    expose
  }) {
    const {
      okClosable,
      cancelClosable
    } = toRefs(props);
    const modalTools = ref([]);
    const componentParams = reactive({
      ...props.params
    });
    const modalRef = ref();
    const buttonProps = reactive({
      ok: {
        title: unref(props.okText) || '确定',
        icon: 'vxe-icon-check',
        type: 'primary',
        loading: unref(props.confirmLoading),
        disabled: false,
        visible: unref(props.showOkButton),
        dropdowns: []
      },
      cancel: {
        title: unref(props.cancelText) || '取消',
        icon: 'vxe-icon-close',
        type: 'danger',
        loading: false,
        disabled: false,
        visible: unref(props.showCancelButton),
        dropdowns: []
      }
    });
    const buttonEvents = {
      ok: {
        click: ({
          $event
        }) => {
          emit('ok', $event);
          unref(okClosable) && !!modalRef.value && modalRef.value.close();
        }
      },
      cancel: {
        click: ({
          $event
        }) => {
          emit('cancel', $event);
          unref(cancelClosable) && !!modalRef.value && modalRef.value.close();
        }
      }
    };
    const setModalTools = items => {
      modalTools.value = (items || []).map(item => {
        const {
          title,
          type,
          danger,
          onClick,
          icon = '',
          dropdowns = [],
          loading,
          disabled
        } = item;
        return {
          title,
          type,
          danger,
          onClick,
          icon,
          dropdowns,
          loading,
          disabled
        };
      });
    };
    const exposeMethods = {
      updateFooter: props => {
        const {
          okDisabled,
          okText,
          okLoading,
          okVisible,
          cancelVisible,
          cancelDisabled,
          cancelText,
          cancelLoading,
          buttons: newButtons
        } = props;
        if (!!newButtons && Array.isArray(newButtons)) {
          setModalTools(newButtons);
        }
        Object.assign(buttonProps.ok, omit({
          visible: unref(okVisible),
          disabled: unref(okDisabled),
          loading: unref(okLoading),
          title: unref(okText)
        }));
        Object.assign(buttonProps.cancel, omit({
          visible: unref(cancelVisible),
          disabled: unref(cancelDisabled),
          loading: unref(cancelLoading),
          title: unref(cancelText)
        }));
      },
      // Deprecated in 1.0.1, use setTools instead
      setButtons: items => {
        setModalTools(items);
      },
      setTools: items => {
        setModalTools(items);
      },
      updateOkProps: props => {
        const {
          text,
          disabled,
          loading,
          visible
        } = props || {};
        const newProps = omit({
          title: unref(text),
          disabled: unref(disabled),
          loading: unref(loading),
          visible: unref(visible)
        });
        Object.assign(buttonProps.ok, newProps);
      },
      updateCancelProps: props => {
        const {
          text,
          disabled,
          loading,
          visible
        } = props || {};
        const newProps = omit({
          title: unref(text),
          disabled: unref(disabled),
          loading: unref(loading),
          visible: unref(visible)
        });
        Object.assign(buttonProps.cancel, newProps);
      },
      ok: e => {
        return buttonEvents.ok.click({
          $event: e
        });
      },
      cancel: e => {
        return buttonEvents.cancel.click({
          $event: e
        });
      }
    };
    provide('x-dialog-context', exposeMethods);
    const modalEvents = {
      close() {
        emit('close');
      },
      hide({
        type
      }) {
        emit('update:open', false);
        emit('hide', {
          type
        });
      },
      move({
        type,
        $event
      }) {
        emit('move', {
          type,
          $event
        });
      },
      resize({
        type,
        $event
      }) {
        emit('resize', {
          type,
          $event
        });
      },
      show({
        type
      }) {
        emit('update:open', true);
        emit('show', {
          type
        });
      },
      zoom({
        type,
        $event
      }) {
        emit('zoom', {
          type,
          $event
        });
      }
    };
    const modalProps = reactive({
      open: props.open,
      title: props.title,
      width: props.width,
      okText: props.okText,
      cancelText: props.cancelText,
      okClosable: props.okClosable,
      cancelClosable: props.cancelClosable,
      showFooter: props.showFooter,
      showMaximize: props.showMaximize,
      showMinimize: props.showMinimize,
      showClose: props.showClose,
      destroyOnClose: props.destroyOnClose,
      transfer: props.transfer,
      draggable: props.draggable,
      resize: props.resize,
      keyboard: props.keyboard,
      lockView: props.lockView,
      mask: props.mask,
      maskClosable: props.maskClosable,
      showOkButton: props.showOkButton,
      showCancelButton: props.showCancelButton,
      fullscreen: props.fullscreen,
      beforeHide({
        type
      }) {
        if (props.beforeHide && typeof props.beforeHide === 'function') {
          return props.beforeHide({
            type
          });
        }
        return null;
      }
    });
    const modalMethods = {
      open(tit, compProps = {}) {
        modalProps.title = unref(tit);
        Object.assign(componentParams, compProps || {});
        !!modalRef.value && modalRef.value.open();
      },
      close() {
        !!modalRef.value && modalRef.value.close();
      },
      getBox() {
        if (!modalRef.value) {
          return modalRef.value.getBox();
        }
        return null;
      },
      getPosition() {
        if (!modalRef.value) {
          return modalRef.value.getPosition();
        }
        return {
          top: -1,
          left: -1
        };
      },
      setPosition(top, left) {
        !!modalRef.value && modalRef.value.setPosition(top, left);
      },
      isMaximized() {
        if (!modalRef.value) {
          return modalRef.value.isMaximized();
        }
        return false;
      },
      zoom() {
        !!modalRef.value && modalRef.value.zoom();
      },
      minimize() {
        !!modalRef.value && modalRef.value.minimize();
      },
      maximize() {
        !!modalRef.value && modalRef.value.maximize();
      },
      revert() {
        !!modalRef.value && modalRef.value.revert();
      }
    };
    expose({
      ...exposeMethods,
      ...modalMethods
    });
    return {
      modalRef,
      modalProps,
      modalEvents,
      buttonProps,
      buttonEvents,
      modalTools,
      componentParams,
      appTheme,
      componentSize,
      locale
    };
  }
};